import { useState } from 'react';
import { Play } from 'lucide-react';
import Loop from './Loop';
import videosData from '../data/videos.json';

/**
 * Featured AI Crossfields videos, directly under the hero on the home page only.
 * Reads from src/data/videos.json, so swapping videos is a one-file edit.
 *
 *  - The videos run as an endless loop, the same pattern as the Introductions
 *    case studies. The loop pauses while a video in it is playing.
 *  - Each card shows the YouTube thumbnail and only loads the player (from
 *    youtube-nocookie.com) once someone presses play, so the page stays light.
 *  - Renders nothing while the list is empty.
 */
type Clip = { id: string; title?: string };

type VideosFile = {
  channel: { name: string; handle: string; url: string };
  items: Clip[];
};

const data = videosData as VideosFile;

/** Repeat a short list so one half of the loop is wider than a large screen. */
function fill(list: Clip[], min: number): Clip[] {
  if (list.length === 0) return list;
  const out: Clip[] = [];
  while (out.length < min) out.push(...list);
  return out;
}

export default function FeaturedVideos() {
  const videos = data.items ?? [];
  if (videos.length === 0) return null;
  const { channel } = data;

  const videoLoop = fill(videos, 6);

  return (
    <section aria-label={`${channel.name} videos`} className="bg-black py-24">
      <div className="container-x">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="font-mono text-xs uppercase tracking-label text-gold">
              {channel.name} on YouTube
            </p>
            <h2 className="mt-3 font-display text-3xl font-light text-white">
              Watch {channel.name}.
            </h2>
          </div>
          <a
            href={channel.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex w-fit items-center gap-2 rounded-full border border-gold/60 px-4 py-1.5 font-sans text-[0.7rem] uppercase tracking-label text-white transition hover:bg-gold hover:text-black"
          >
            {channel.handle}
            <span className="text-gold transition-colors group-hover:text-black">→</span>
          </a>
        </div>
      </div>

      {videoLoop.length > 0 && (
        <Loop seconds={videoLoop.length * 8} label="Videos" interactive className="mt-10">
          {(copy) =>
            videoLoop.map((clip, index) => (
              <ClipCard
                key={`${copy}-${clip.id}-${index}`}
                clip={clip}
                testId={copy === 0 && index < videos.length ? 'featured-video' : undefined}
                hidden={copy === 1}
              />
            ))
          }
        </Loop>
      )}

    </section>
  );
}

function ClipCard({
  clip,
  testId,
  hidden,
}: {
  clip: Clip;
  testId?: string;
  /** Second loop copy: still clickable, but out of the tab order. */
  hidden?: boolean;
}) {
  const [playing, setPlaying] = useState(false);
  const label = clip.title ? `Play video: ${clip.title}` : 'Play video';
  const size = 'w-[19rem] sm:w-[26rem] aspect-video';

  return (
    <div data-testid={testId} className="mr-5 flex flex-none flex-col">
      <div
        className={`relative overflow-hidden rounded-3xl border border-white/10 bg-charcoal ${size}`}
      >
        {playing ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${clip.id}?autoplay=1&rel=0`}
            title={clip.title ?? 'AI Crossfields video'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 h-full w-full"
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label={label}
            tabIndex={hidden ? -1 : undefined}
            className="group absolute inset-0 h-full w-full"
          >
            <img
              src={`https://i.ytimg.com/vi/${clip.id}/hqdefault.jpg`}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover opacity-80 transition duration-500 group-hover:scale-[1.03] group-hover:opacity-100"
            />
            <span className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <span className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-gold/70 bg-black/60 text-gold backdrop-blur-sm transition group-hover:bg-gold group-hover:text-black">
              <Play size={20} className="translate-x-px" fill="currentColor" />
            </span>
          </button>
        )}
      </div>
      {clip.title && (
        <p className="mt-3 w-[19rem] font-sans text-sm leading-relaxed text-white/85 sm:w-[26rem]">
          {clip.title}
        </p>
      )}
    </div>
  );
}
